import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchAllMemoIndexes, fetchMemoIndex, memoIndexKeys } from '../api/memoIndexAPI';
import { useNotificationProcessingState } from '../managers/useNotificationProcessingState';
import { handleError, useErrorHandler } from '../managers/errorHandler';

/**
 * Memo Index查询hooks - 统一命名规范
 */

// Memo Index列表查询 - 统一命名规范
export const useMemoIndexListQuery = (options?: { enabled?: boolean }) => {
  const { handleError } = useErrorHandler();
  // 通知消息处理期间，避免自动触发index查询，由消息处理函数统一控制
  const isNotificationProcessing = useNotificationProcessingState((state) => state.isProcessing);
  
  return useQuery({
    queryKey: memoIndexKeys.list(),
    queryFn: async () => {
      try {
        const response = await fetchAllMemoIndexes();
        return response || [];
      } catch (error) {
        handleError(error, { context: '获取memo indexes失败' });
        throw error;
      }
    },
    enabled: (options?.enabled !== undefined ? options.enabled : true) && !isNotificationProcessing,
    staleTime: 5 * 60 * 1000, // 5分钟
    gcTime: 10 * 60 * 1000, // 10分钟
  });
};

// 单个Memo Index查询 - 统一命名规范
export const useMemoIndexQuery = (memoId: string, options?: { enabled?: boolean }) => {
  const { handleError } = useErrorHandler();
  // 通知消息处理期间，避免自动触发index查询，由消息处理函数统一控制
  const isNotificationProcessing = useNotificationProcessingState((state) => state.isProcessing);
  
  return useQuery({
    queryKey: memoIndexKeys.detail(memoId),
    queryFn: async () => {
      if (!memoId) {
        return null;
      }
      
      try {
        const response = await fetchMemoIndex(memoId);
        return response || null;
      } catch (error) {
        handleError(error, { context: `获取memoId为"${memoId}"的memo index失败` });
        throw error;
      }
    },
    enabled: !!memoId && (options?.enabled !== undefined ? options.enabled : true) && !isNotificationProcessing,
    staleTime: 5 * 60 * 1000, // 5分钟
    gcTime: 10 * 60 * 1000, // 10分钟
  });
};

/**
 * Memo Index操作mutation hooks - 统一命名规范
 */

// 刷新所有memo indexes
export const useRefreshAllMemoIndexesMutation = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();
  
  return useMutation({
    mutationFn: async () => {
      try {
        // 清除缓存并重新获取
        await queryClient.invalidateQueries({ queryKey: memoIndexKeys.all });
        const response = await fetchAllMemoIndexes();
        return response || [];
      } catch (error) {
        handleError(error, { context: '刷新memo indexes失败' });
        throw error;
      }
    },
    onSuccess: (data) => {
      // 使相关查询失效，由Manager层处理数据同步
      queryClient.invalidateQueries({ queryKey: memoIndexKeys.list() });
    },
  });
};

// 刷新特定memoId的memo index
export const useRefreshMemoIndexByGuidMutation = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();
  
  return useMutation({
    mutationFn: async (memoId: string) => {
      if (!memoId) {
        handleError(new Error('memoId参数不能为空'), { context: '刷新memo index失败' });
        throw new Error('memoId参数不能为空');
      }
      
      try {
        // 清除缓存并重新获取
        await queryClient.invalidateQueries({ queryKey: memoIndexKeys.detail(memoId) });
        const response = await fetchMemoIndex(memoId);
        return response || null;
      } catch (error) {
        handleError(error, { context: `刷新memoId为"${memoId}"的memo index失败` });
        throw error;
      }
    },
    onSuccess: (data, memoId) => {
      // 使相关查询失效，由Manager层处理数据同步
      if (memoId) {
        queryClient.invalidateQueries({ queryKey: memoIndexKeys.detail(memoId) });
      }
    },
  });
};